<template>
    <div class="grid grid-cols-2 gap-4 bg-slate-50 dark:bg-slate-900/40 rounded-lg p-4">
        <div
            class="col-span-2 justify-between items-center flex"
            v-show="activeRedemption.transfers.length > 1"
        >
            <div class="text-slate-800 dark:text-slate-100 text-sm font-medium">
                {{ `Points transfer ${index + 1}` }}
            </div>
            <button
                @click="removeTransfer(index)"
                class="flex rounded justify-start items-center gap-2 text-slate-800 dark:text-slate-100 focus:outline-none active:outline-none hover:border-slate-800 focus:ring-2 focus:ring-slate-200"
            >
                <Icon icon="trash" />
                <p class="text-slate-800 dark:text-slate-100 text-sm font-medium">Delete</p>
            </button>
        </div>
        <div class="col-span-1">
            <Input
                label="Amount of points transferred"
                placeholder="Amount of points transferred"
                id="transfer-points-used"
                v-model:inputValue="activeRedemption.transfers[index].transfer_points"
                :inputFormat="'thousands'"
                :pattern="'/[0-9]+/'"
                :options="{
                    inputClass: 'h-10 text-sm text-slate-500 dark:text-slate-400',
                }"
            />
        </div>
        <div class="col-span-1 mb-3">
            <h6
                class="block text-slate-600 dark:text-slate-300 text-sm font-medium leading-tight mb-2"
                for="points-currency"
            >
                Original points currency
            </h6>
            <TransferProgramsDropdown
                v-model:currentProgramId="activeRedemption.transfers[index].transfer_program_id"
                :tripFormErrors="tripFormErrors"
            />
        </div>

        <Input
            label="Transfer bonus"
            placeholder="Transfer bonus"
            id="transfer-bonus"
        >
            <template #input>
                <div class="flex">
                    <div
                        class="flex items-center justify-center bg-white dark:bg-slate-800 px-3 rounded-l border text-slate-500 dark:text-slate-400 border-slate-200 dark:border-slate-700"
                    >
                        %
                    </div>
                    <input
                        :id="`transfer-bonus-${index}`"
                        class="w-full h-10 text-sm rounded-r border-t border-b border-r border-l-0 border-slate-200 dark:border-slate-700"
                        type="number"
                        pattern="[0-9]*"
                        step="1"
                        placeholder="Transfer bonus"
                        v-model="activeRedemption.transfers[index].transfer_bonus_percentage"
                    />
                </div>
            </template>
        </Input>

        <div
            v-if="transferRate > 0"
        >
            <h6
                class="block text-slate-600 dark:text-slate-300 text-sm font-medium leading-tight mt-0 mb-4"
            ><span>{{
                    `Total ${getProgramName(transfer.transfer_program_id)} points transferred`}}</span>
                <Transition name="fade"><span v-if="transfer.transfer_bonus_percentage"> (with bonus)</span></Transition>
            </h6>
            <p class="block text-slate-500 dark:text-slate-400 text-base">
                {{ Math.round(calculatedTransferPoints).toLocaleString() }}
            </p>
        </div>

    </div>
</template>
<script setup>
import { computed, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useTripsStore } from '@/store/trips';
import Icon from '@/components/ui/icon/icon.vue';
import Input from '@/components/ui/inputs/ui-input.vue';
import TransferProgramsDropdown from '@/components/trips/modals/steps/transfer-programs-dropdown.vue';
const tripsStore = useTripsStore();
const props = defineProps({
    transfer: {
        type: Object,
        required: true,
    },
    index: {
        type: Number,
        required: true,
    },
});
const { activeRedemption, programs, transferPartners, tripFormErrors } =
    storeToRefs(tripsStore);

const getProgramName = (id) => {
    return programs.value.find((p) => p.id === id)?.name || '';
};

const removeTransfer = (index) => {
    activeRedemption.value.transfers.splice(index, 1);
};

const transferRate = computed(() => {
    const relevantTransferPartner = transferPartners.value.find(
        (tp) =>
            tp.source_program_id === props.transfer.transfer_program_id &&
            tp.destination_program_id ===
                activeRedemption.value.payment_program_id
    );

    const transferRate = relevantTransferPartner?.transfer_ratio ?? 0;

    return transferRate;
});

const calculatedTransferPoints = computed(() => {
    const points = Number(props.transfer.transfer_points) || 0;
    const bonus = Number(props.transfer.transfer_bonus_percentage) || 0;
    const rate = transferRate.value;

    if (points === 0 || rate === 0) return 0;

    const basePoints = rate * points;
    return bonus ? basePoints * (1 + bonus / 100) : basePoints;
});

watch(
    () => activeRedemption.value.transfers,
    () => {
        tripsStore.clearErrors();
    },
    { deep: true }
);
</script>

